import { useState } from 'react';
import { type ScoringScale } from '../types';
import { ArrowRight } from 'lucide-react';
import { SCORING_SCALE_CHOICES, scoringScaleLabel } from '../../../lib/toolSettings';

interface ScaleStepProps {
  scale: ScoringScale;
  onNext: (scale: ScoringScale) => void;
}

export function ScaleStep({ scale, onNext }: ScaleStepProps) {
  const [min, setMin] = useState(scale.min);
  const [max, setMax] = useState(scale.max);

  const handlePreset = (presetMin: number, presetMax: number) => {
    setMin(presetMin);
    setMax(presetMax);
  };

  const handleNext = () => {
    if (min >= max) {
      alert('Minimum must be less than maximum.');
      return;
    }
    onNext({ min, max, higherIsBetter: true });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
          Choose Scoring Scale
        </h2>
        <p className="text-sm text-[var(--text-sec)] mb-6">
          Select the numeric scale you'll use to score each item on each criterion.
        </p>

        <div className="space-y-4 mb-6">
          {/* The workspace's standard scales — the same ones a session's setup offers. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SCORING_SCALE_CHOICES.map((choice) => (
              <button
                key={`${choice.min}-${choice.max}`}
                type="button"
                onClick={() => handlePreset(choice.min, choice.max)}
                className={`px-4 py-3 border-2  transition-colors ${
                  min === choice.min && max === choice.max
                    ? 'border-[var(--slate)] bg-[var(--slate-dim)]'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="font-semibold text-[var(--text-pri)]">{scoringScaleLabel(choice)}</div>
                <div className="text-xs text-[var(--text-sec)] mt-1">{choice.hint}</div>
              </button>
            ))}
          </div>

          <div className="pt-4 border-t border-[var(--rule)]">
            <p className="text-sm font-medium text-[var(--text-sec)] mb-3">Custom Scale</p>
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-xs text-[var(--text-sec)] mb-1">Minimum</label>
                <input
                  type="number"
                  value={min}
                  onChange={(e) => setMin(parseInt(e.target.value) || 1)}
                  min="1"
                  className="w-20 px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <span className="text-[var(--text-ter)] mt-6">to</span>
              <div>
                <label className="block text-xs text-[var(--text-sec)] mb-1">Maximum</label>
                <input
                  type="number"
                  value={max}
                  onChange={(e) => setMax(parseInt(e.target.value) || 5)}
                  min={min + 1}
                  className="w-20 px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 bg-[var(--slate-dim)] border border-[var(--slate)] mb-6">
          <p className="text-sm text-[var(--text-sec)]">
            <strong>Note:</strong> Higher scores always mean "better" performance on the criterion.
            For cost/effort criteria, score low values (e.g., low effort = 5, high effort = 1).
          </p>
        </div>

        <div className="mt-6 pt-4 border-t border-[var(--rule)]">
          <button
            onClick={handleNext}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors"
          >
            Continue
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

